import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { CallToAction } from '../../components/common/blocks';
import AccordionList, {
  IAccordionListItem,
} from '../../components/common/blocks/AccordionList';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../../components/common/blocks/ReviewsCarousel';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import { HeroCountry, SellingPoints } from '../../components/country';
import { IHeroCountry } from '../../components/country/HeroCountry';
import KeyFacts, { IKeyFactsProps } from '../../components/country/KeyFacts';
import RelatedServices, {
  IRelatedService,
} from '../../components/country/RelatedServices';
import RequestCallToAction, {
  IRequestCallToActionProps,
} from '../../components/country/RequestCallToAction';
import { ISellingPoints } from '../../components/country/SellingPoints';
import { useAppSelector } from '../../hooks';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IApplicationClassPrice,
  IBusinessKeyValuesComponent,
  ICallToActionComponent,
  IContactMethod,
  IContactUsForm,
  ICountryPage,
  ICountryRelatedService,
  ICurrency,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  IGenericServiceLanding,
  ILayoutPageProps,
  IMedia,
  IMetaComponent,
  ITrustedByComponent,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getAppCurrencies,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateFaqsSchema,
  generateOrganizationSchema,
  generateProductSchema,
  generateServiceSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import { replaceContentPlaceholders } from '../../shared/useTransformations';
import {
  addCurrencySymbol,
  applyCurrencyConversionRate,
  calculateServicePricesWithCurrency,
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  generateRandomId,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
  keepObjectProperties,
  removeObjectProperties,
} from '../../shared/useUtils';
import { IAppPage } from '../../store/slices/appSlice';
import { NextPageWithLayout } from '../_app';

interface ITrademarkRenewalLandingProps extends ILayoutPageProps {
  heroTipsTitle: string;
  heroTipOne: string;
  heroTipTwo: string;
  heroTipThree: string;
  businessKeyValues: IBusinessKeyValuesComponent;
  trustedBy: ITrustedByComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  heroHeader: string;
  heroHeaderHighlightedText?: string | null;
  heroSubTitle: string;
  serviceTitle: string;
  serviceText: string;
  body: string;
  fromLabel: string;
  orderNowLabel: string;
  fromPrice: number;
  formSuccessMessage: string;
  formErrorMessage: string;
  contactMethods: IContactMethod[];
  contactUsForm: IContactUsForm;
  countryIso: string;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  relatedServicesTitle: string;
  relatedServices: IRelatedService[];
  feesIncludedLabel: string;
  faqsTitle?: string;
  faqs?: IAccordionListItem[];
  pageSchema: PageJSONLDSchema;
  featuredServicesCallToActionProps: ICallToActionProps;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  countryApplicationClassPrices: IApplicationClassPrice[];
  countrySearchClassPrices: IApplicationClassPrice[];
  finalFees: IApplicationClassPrice[];
  estimatedProcessingMonths?: string;
  yearsOfValidity?: string;
  requiredDocuments?: string;
  publicPriceNotes?: string;
  countryKeyFactsProps: IKeyFactsProps;
}

const TrademarkRenewalLandingPage: NextPageWithLayout<
  ITrademarkRenewalLandingProps
> = (props: ITrademarkRenewalLandingProps) => {
  const router = useRouter();
  const appState = useAppSelector((state) => state.app);
  const globalCurrency: ICurrency | undefined = useAppSelector(
    (state) => state.app.currentCurrency
  );
  const [hydrated, setHydrated] = useState(false);

  const calculateServiceFromPrice = useCallback(
    (fromPrice: number) => {
      const calculatedPrice = !!fromPrice
        ? addCurrencySymbol(
            applyCurrencyConversionRate(
              fromPrice,
              globalCurrency?.conversionRate
            ),
            appState.currentCurrency?.isoCode || 'USD',
            appState.currentLocale || 'en'
          )
        : '-';
      return calculatedPrice;
    },
    [
      appState.currentCurrency?.isoCode,
      appState.currentLocale,
      globalCurrency?.conversionRate,
    ]
  );

  const calculateServiceCurrencyAndPrice = useCallback(
    (
      applicationClassPrices: IApplicationClassPrice[],
      searchClassPrices: IApplicationClassPrice[],
      finalFees: IApplicationClassPrice[]
    ) => {
      return calculateServicePricesWithCurrency(
        { applicationClassPrices, searchClassPrices, finalFees },
        globalCurrency?.conversionRate || 1,
        appState.currentCurrency?.isoCode || 'USD',
        appState.currentLocale || 'en'
      );
    },
    [
      appState.currentCurrency?.isoCode,
      appState.currentLocale,
      globalCurrency?.conversionRate,
    ]
  );

  const scrollToContactUs = () => {
    const element = document.getElementById('contact-us');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const heroCountryProps: IHeroCountry = {
    title: props.heroHeader,
    titleHighlightedText: props.heroHeaderHighlightedText,
    countryCode: props.countryIso,
    tips: [
      {
        text: `**${props.customerReviewsMeta.average}** (+${props.customerReviewsMeta.numberOfReviews})`,
        iconPath: '/assets/images/icons/StarRounded.svg',
      },
      {
        text: props.heroTipOne,
        iconPath: '/assets/images/icons/ClockIcon.svg',
      },
      {
        text: props.heroTipTwo,
        iconPath: '/assets/images/icons/PersonIcon.svg',
      },
      {
        text: props.heroTipThree,
        iconPath: '/assets/images/icons/CoinsIcon.svg',
      },
    ],
  };

  const requestCallToActionProps: IRequestCallToActionProps = {
    text: props.heroSubTitle,
    fromPrice: calculateServiceFromPrice(props.fromPrice),
    fromLabel: props.fromLabel,
    feesIncludedLabel: props.feesIncludedLabel,
    button: {
      text: props.orderNowLabel,
      action: scrollToContactUs,
    },
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || `Partner logo ${x.name}`}
          loading="lazy"
        />
      ),
    })),
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    bottomTip: props.customerReviewsBottomTip,
    defaultLinkPath: props.customerReviews.buttonPath,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.formSuccessMessage,
      submitErrorMessage: props.formErrorMessage,
    },
  };

  const faqsItems: IAccordionListItem[] =
    props.faqs?.map((faq) => {
      const prices = calculateServiceCurrencyAndPrice(
        props.countryApplicationClassPrices,
        props.countrySearchClassPrices,
        props.finalFees
      );
      const placeholderValues = {
        estimatedProcessingMonths: props.estimatedProcessingMonths,
        yearsOfValidity: props.yearsOfValidity,
        requiredDocuments: props.requiredDocuments,
        publicPriceNotes: props.publicPriceNotes,
      };
      const faqContent = replaceContentPlaceholders(faq.content, {
        ...prices,
        ...placeholderValues,
      });

      return {
        ...faq,
        content: faqContent,
      };
    }) || [];

  useEffect(() => {
    setHydrated(true);
  }, []);

  const DynamicTrustedBy = dynamic(
    () => import('../../components/common/blocks/TrustedBy'),
    {
      ssr: false,
    }
  );

  const DynamicContact = dynamic(
    () => import('../../components/contact-us/Contact'),
    {
      ssr: false,
    }
  );

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <section className="page-content-section">
        <header>
          <div className="page-content-block">
            <HeroCountry {...heroCountryProps} />
          </div>
          <div className="page-content-block">
            <RequestCallToAction {...requestCallToActionProps} />
          </div>
          {props.countryKeyFactsProps &&
            props.countryKeyFactsProps.facts?.length > 0 && (
              <div className="page-content-block">
                <KeyFacts {...props.countryKeyFactsProps} />
              </div>
            )}
        </header>
      </section>

      {props.faqs && props.faqs.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={faqsItems} />
          </div>
        </div>
      )}

      <div className="page-content-section">
        <div className="page-content-block" id="customer-reviews">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      {/* <div className="page-content-section background-light">
        <div className="page-content-block">
          <DynamicTrustedBy {...trustedByProps} />
        </div>
      </div> */}

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact-us">
          <DynamicContact {...contactProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      {!!props.body && props.body !== 'n/a' && (
        <div className="page-content-section">
          <div className="page-content-block is-text-align-justified-mobile">
            {' '}
            <ReactMarkdown>{props.body}</ReactMarkdown>
          </div>
        </div>
      )}

      {props.relatedServices.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <RelatedServices
              title={props.relatedServicesTitle}
              services={props.relatedServices}
            />
          </div>
        </div>
      )}

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToAction {...props.featuredServicesCallToActionProps} />
        </div>
      </div>
    </div>
  );
};

TrademarkRenewalLandingPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      noindex={page.props.meta.noindex}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

function fallbackPageMeta(
  locale: string,
  meta: IMetaComponent[],
  countryCode: string
) {
  const localizedMeta = getTranslation(meta, locale);
  const fallback = {
    title: localizedMeta.title || generateRandomId(),
    description: localizedMeta.description || generateRandomId(),
    slug: !!localizedMeta.slug
      ? `${localizedMeta.slug}/${countryCode}`
      : generateRandomId(),
    language: locale,
  };
  return fallback;
}

const entityBaseName = 'trademark-renewal';

export async function getStaticPaths(context: any) {
  let paths: any[] = [];
  const landingsUrlParams = new URLSearchParams({
    'populate[0]': 'metas',
    'pagination[start]': '0',
    'pagination[limit]': '-1',
  });

  const landings: IGenericServiceLanding[] = flatStrapiCollection(
    await fetchStrapi<any>(
      `/${entityBaseName}-landings`,
      landingsUrlParams,
      buildApiKey
    )
  );

  for (const locale of context.locales) {
    const localizedPaths = landings.map((item: IGenericServiceLanding) => {
      const localizedMeta = getTranslation(item.metas, locale);
      return {
        params: {
          slug: localizedMeta.slug,
        },
        locale: locale,
      };
    });
    paths = [...paths, ...localizedPaths];
  }

  return { paths, fallback: false };
}

export async function getStaticProps(
  context: any
): Promise<{ props: ITrademarkRenewalLandingProps }> {
  const appRoutes = await getRoutes();

  const getLocalizedAppRoute = (actualPath: string) => {
    return (
      appRoutes?.find(
        (x: IAppPage) =>
          x &&
          x.destination.replace(`/${context.locale}`, '') === actualPath &&
          x.language === context.locale
      )?.source || actualPath
    );
  };

  const paramSlug = context.params.slug;

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'heroTipsTitle',
    'populate[2]': 'heroTipOne',
    'populate[3]': 'heroTipTwo',
    'populate[4]': 'heroTipThree',
    'populate[5]': 'businessKeyValues',
    'populate[6]': 'trustedBy',
    'populate[7]': 'trustedBy.logos',
    'populate[8]': 'customerReviews',
    'populate[9]': 'fromLabel',
    'populate[10]': 'orderNowLabel',
    'populate[11]': 'contactUsForm',
    'populate[12]': 'formSuccessMessage',
    'populate[13]': 'formErrorMessage',
    'populate[14]': 'relatedServicesTitle',
    'populate[15]': 'requiredDocuments',
    'populate[16]': 'publicPriceNotes',
    'populate[17]': 'countryKeyFactsRenewals',
    'populate[18]': 'countryKeyFactsRenewals.facts',
    'populate[19]': 'countryKeyFactsRenewals.facts.icon',
  });

  const landingUrlParams = new URLSearchParams({
    'filters[metas][slug][$eq]': paramSlug,
    'populate[0]': 'country',
    'populate[1]': 'country.searchClassPrices',
    'populate[2]': 'country.applicationClassPrices',
    'populate[3]': 'country.finalFees',
    'populate[4]': 'country.currency',
    'populate[5]': 'country.nameTranslations',
    'populate[6]': 'metas',
    'populate[7]': 'heroHeaders',
    'populate[8]': 'heroHeadersHighlightedText',
    'populate[9]': 'heroSubTitles',
    'populate[10]': 'serviceTitles',
    'populate[11]': 'serviceTexts',
    'populate[12]': 'bodies',
    'populate[13]': 'faqs',
    'populate[14]': 'breadcrumbAncestors',
    'populate[15]': 'faqsTitle',
    'populate[16]': 'country.requiredDocuments',
    'populate[17]': 'country.publicPriceNotes',
    'populate[18]': 'countryKeyFacts',
    'populate[19]': 'countryKeyFacts.facts',
    'populate[20]': 'countryKeyFacts.facts.icon',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  const countryBasePage = (
    await fetchStrapi<any>(`/country-page`, pageUrlParams, buildApiKey)
  ).data.attributes as ICountryPage;
  const landingsResult = await fetchStrapi<any>(
    `/${entityBaseName}-landings`,
    landingUrlParams,
    buildApiKey
  );
  const landing: IGenericServiceLanding =
    flatStrapiCollection<IGenericServiceLanding>(landingsResult)[0];
  landing.country = flatStrapiObject(landing.country as any);

  const reviews = (
    flatStrapiCollection(
      await fetchStrapi<any>(
        `/customer-reviews`,
        customerReviewsUrlParams,
        buildApiKey
      )
    ) as ICustomerReview[]
  ).map((x) => removeObjectProperties(x, ['id', 'createdAt', 'updatedAt']));

  const trustedBy: ITrustedByComponent = getTranslation(
    countryBasePage.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );

  const customerReviews: ICustomerReviewsComponent = getTranslation(
    countryBasePage.customerReviews,
    context.locale
  );
  const contactMethods = (await getContactMethods()).map((x) =>
    removeObjectProperties(x, ['id', 'createdAt', 'updatedAt'])
  );
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  navBarProps.items = removeObjectProperties(navBarProps.items, [
    'id',
    'createdAt',
    'updatedAt',
  ]);

  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = removeObjectProperties(
    await populateFooterProps(context.locale),
    ['id', 'createdAt', 'updatedAt']
  );

  // trademarkLanding → clean up
  const pageMeta =
    getTranslation(landing.metas, context.locale) ||
    fallbackPageMeta(
      context.locale,
      countryBasePage.meta,
      landing.country?.iso || ''
    );
  const originalPageMeta = getTranslation(landing.metas, 'en');

  const heroHeader = getTranslation(landing.heroHeaders, context.locale)
    .text as string;
  const heroHeaderHighlightedText =
    landing.heroHeadersHighlightedText &&
    landing.heroHeadersHighlightedText.length > 0
      ? (getTranslation(landing.heroHeadersHighlightedText, context.locale)
          .text as string)
      : null;
  const heroSubTitle = getTranslation(landing.heroSubTitles, context.locale)
    .text as string;
  const serviceTitle = getTranslation(landing.serviceTitles, context.locale)
    .text as string;
  const serviceText = getTranslation(landing.serviceTexts, context.locale)
    .text as string;
  const body = getTranslation(landing.bodies, context.locale).text as string;
  const alternativeLinks = getAlternateLinks(
    `/trademark-renewal/${originalPageMeta.slug}`,
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', pageMeta.slug);
  const commonTranslations = await getCommonTranslations();

  const countryRelatedServicesUrlParams = {
    iso: landing.country.iso,
    locale: context.locale,
    apiNamesToExclude: [
      `${entityBaseName}-landing`,
      'copyright-landing',
      'industrial-design-landing',
    ],
  };
  const countryRelatedServices = await fetchStrapi<ICountryRelatedService[]>(
    `/countries/related-services`,
    countryRelatedServicesUrlParams,
    buildApiKey
  );

  const faqsTitle =
    landing.faqsTitle && landing.faqsTitle.length > 0
      ? (getTranslation(landing.faqsTitle, context.locale).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = landing?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  // Generate JSON-LD schema
  const currencies = await getAppCurrencies();
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const serviceSchema = generateServiceSchema(
    landing,
    currencies,
    canonicalLink,
    context.locale
  );

  // FAQs schema generation --- START
  // The following elements are used to replace FAQs placeholders with actual values
  const countryApplicationClassPrices = landing.country
    .applicationClassPrices as IApplicationClassPrice[];

  const countrySearchClassPrices = landing.country
    .searchClassPrices as IApplicationClassPrice[];

  const finalFees = landing.country.finalFees as IApplicationClassPrice[];

  // Because the currency is dynamic and can be changed by the user,
  // we need to calculate the prices with the default currency (USD) and conversion rate
  const prices = calculateServicePricesWithCurrency(
    {
      applicationClassPrices: countryApplicationClassPrices,
      searchClassPrices: countrySearchClassPrices,
      finalFees,
    },
    1,
    'USD',
    context.locale
  );

  const estimatedProcessingMonths = landing.country.estimatedProcessingMonths
    ? landing.country.estimatedProcessingMonths.toString()
    : '';

  const yearsOfValidity = landing.country.yearsOfValidity
    ? landing.country.yearsOfValidity.toString()
    : '';

  const requiredDocuments = getTranslation(
    landing.country.requiredDocuments,
    context.locale
  ).text as string;

  const publicPriceNotes = getTranslation(
    landing.country.publicPriceNotes,
    context.locale
  ).text as string;

  const placeholderValues = {
    estimatedProcessingMonths,
    yearsOfValidity,
    requiredDocuments,
    publicPriceNotes,
    average: customerReviewsMeta.average,
    numberOfReviews: customerReviewsMeta.numberOfReviews,
    bestRating: customerReviewsMeta.bestRating,
  };

  const faqsSchema = generateFaqsSchema(
    faqs.map((x) => ({
      question: x.title,
      answer: replaceContentPlaceholders(x.content, {
        ...prices,
        ...placeholderValues,
      }),
    }))
  );
  // FAQs schema generation --- END
  const productSchema = await generateProductSchema(
    heroHeader,
    customerReviewsMeta
  );
  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [
      ...websiteSchema,
      ...organizationSchema,
      ...serviceSchema,
      ...faqsSchema,
      ...productSchema,
      ...customerReviewsSchema,
    ],
  };

  // Populate breadcrumbs
  if (landing.breadcrumbAncestors && landing.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = landing.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroHeader;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Prepare Featured Services Call To Action component props
  const featuredServicesCallToAction: ICallToActionComponent = getTranslation(
    commonTranslations.featuredServicesCallToAction,
    context.locale
  );

  const featuredServicesCallToActionProps: ICallToActionProps = {
    title: featuredServicesCallToAction.title,
    subtitle: featuredServicesCallToAction.content,
    buttons: featuredServicesCallToAction.buttons?.map((button) => ({
      label: button.label,
      path: button.path,
    })),
  };

  const formSuccessMessage = isUndefinedOrEmpty(
    landing.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormSuccessMessage, context.locale)
        .text as string);
  const formErrorMessage = isUndefinedOrEmpty(landing.contactUsFormErrorMessage)
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(landing.contactUsFormErrorMessage, context.locale)
        .text as string);

  // Key facts generation with fallback to country base page if not available in the landing page
  const countryKeyFactsWithFallback = isUndefinedOrEmpty(
    landing.countryKeyFacts
  )
    ? getTranslation(countryBasePage.countryKeyFactsRenewals, context.locale)
    : getTranslation(landing.countryKeyFacts, context.locale);

  // Regenerate the countryKeyFactsProps to be used in the component, ensuring that the iconUrl is correctly set
  const countryKeyFactsProps: IKeyFactsProps = {
    title: countryKeyFactsWithFallback.title || '',
    facts: (countryKeyFactsWithFallback.facts || []).map((item) => {
      const values = {
        ...prices,
        ...placeholderValues,
      };
      const title = replaceContentPlaceholders(item.title, values);
      const text = replaceContentPlaceholders(item.text, values);
      const tip = replaceContentPlaceholders(item.tip || '', values);
      const icon = flatStrapiObject<IMedia>(item.icon); // Map the icon property to the actual icon object

      return {
        title,
        text,
        tip,
        anchor: item.anchor || '',
        iconUrl: icon.url || '',
      };
    }),
  };

  const props: ITrademarkRenewalLandingProps = {
    // Layout
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta: pageMeta,
    heroHeader,
    heroHeaderHighlightedText,
    heroSubTitle,
    serviceTitle,
    serviceText,
    body,
    heroTipsTitle: getTranslation(countryBasePage.heroTipsTitle, context.locale)
      .text as string,
    heroTipOne: getTranslation(countryBasePage.heroTipOne, context.locale)
      .text as string,
    heroTipTwo: getTranslation(countryBasePage.heroTipTwo, context.locale)
      .text as string,
    heroTipThree: getTranslation(countryBasePage.heroTipThree, context.locale)
      .text as string,
    businessKeyValues: getTranslation(
      countryBasePage.businessKeyValues,
      context.locale
    ),
    trustedBy,
    customerReviews,
    reviews,
    fromLabel: getTranslation(countryBasePage.fromLabel, context.locale)
      .text as string,
    orderNowLabel: getTranslation(countryBasePage.orderNowLabel, context.locale)
      .text as string,
    contactMethods,
    formSuccessMessage,
    formErrorMessage,
    contactUsForm: getTranslation(
      countryBasePage.contactUsForm,
      context.locale
    ),
    countryIso: landing.country.iso,
    fromPrice: landing.fromPrice,
    alternativeLinks,
    canonicalLink,
    relatedServicesTitle: getTranslation(
      countryBasePage.relatedServicesTitle,
      context.locale
    ).text as string,
    relatedServices: countryRelatedServices.map((x) => ({
      title: x.title,
      link: getLocalizedAppRoute(x.slug),
    })),
    feesIncludedLabel: getTranslation(
      commonTranslations.feesIncluded,
      context.locale
    ).text as string,
    faqsTitle,
    faqs,
    pageSchema,
    featuredServicesCallToActionProps,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    countryApplicationClassPrices: landing.country.applicationClassPrices,
    countrySearchClassPrices: landing.country.searchClassPrices,
    finalFees: landing.country.finalFees || [],
    estimatedProcessingMonths: landing.country.estimatedProcessingMonths
      ? landing.country.estimatedProcessingMonths.toString()
      : '',
    yearsOfValidity: landing.country.yearsOfValidity
      ? landing.country.yearsOfValidity.toString()
      : '',
    requiredDocuments: getTranslation(
      landing.country.requiredDocuments,
      context.locale
    ).text as string,
    publicPriceNotes: getTranslation(
      landing.country.publicPriceNotes,
      context.locale
    ).text as string,
    countryKeyFactsProps,
  };

  return {
    props,
  };
}

export default TrademarkRenewalLandingPage;
